<script setup lang="ts">
import { computed } from 'vue'
import { formatNumber } from '@/helpers'
import type { MileageValidationResult } from '@/composables/useMileageValidation'

const props = defineProps<{
  result: MileageValidationResult
}>()

const emit = defineEmits<{
  adjust: [value: number]
  dismiss: []
}>()

const color = computed(() => props.result.type === 'error' ? 'error' : 'warning')
</script>

<template>
  <v-alert
    v-if="result.type !== 'none'"
    :color="color"
    variant="tonal"
    density="comfortable"
    class="mt-3"
  >
    <template v-if="result.type === 'error'">
      Kilometerstanden kan ikke være lavere end ved seneste service ({{ formatNumber(result.kmAtService) }} km).
    </template>
    <template v-else-if="result.type === 'overAnnualCap'">
      Med den faktiske kilometerstand kan aftalen maksimalt dække {{ formatNumber(result.maxAnnual) }} km/år ved den valgte løbetid.
    </template>
    <template v-else-if="result.type === 'implausibleHigh'">
      Siden seneste service svarer det til ca. {{ formatNumber(Math.round(result.impliedAnnual)) }} km om året, hvilket virker usædvanligt højt — tjek lige dine kilometer-tal igen.
    </template>
    <template v-else-if="result.type === 'high'">
      Siden seneste service har du kørt ca. {{ formatNumber(Math.round(result.impliedMonthly)) }} km om måneden, svarende til {{ formatNumber(Math.round(result.impliedAnnual)) }} km om året.
    </template>
    <template v-else-if="result.type === 'low'">
      Du har kun kørt {{ formatNumber(result.kmSince) }} km på {{ result.months }} måneder — er kilometerstanden korrekt?
    </template>

    <div v-if="result.type !== 'error'" class="d-flex ga-2 mt-2">
      <v-btn
        v-if="result.type === 'overAnnualCap'"
        size="small"
        variant="flat"
        @click="emit('adjust', result.maxAnnual)"
      >
        Justér til {{ formatNumber(result.maxAnnual) }} km/år
      </v-btn>
      <v-btn
        v-else-if="result.type === 'implausibleHigh' && result.canSetMax"
        size="small"
        variant="flat"
        @click="emit('adjust', result.maxAnnual)"
      >
        Justér til maks. {{ formatNumber(result.maxAnnual) }} km/år
      </v-btn>
      <v-btn
        v-else-if="result.type === 'high'"
        size="small"
        variant="flat"
        @click="emit('adjust', result.suggested)"
      >
        Justér til {{ formatNumber(result.suggested) }} km/år
      </v-btn>
      <v-btn
        v-else-if="result.type === 'low' && result.suggested != null"
        size="small"
        variant="flat"
        @click="emit('adjust', result.suggested)"
      >
        Justér til {{ formatNumber(result.suggested) }} km/år
      </v-btn>
      <v-btn
        size="small"
        variant="text"
        @click="emit('dismiss')"
      >
        <template v-if="result.type === 'implausibleHigh'">Tallene er korrekte</template>
        <template v-else-if="result.type === 'low'">Kilometerstanden er korrekt</template>
        <template v-else>Behold nuværende valg</template>
      </v-btn>
    </div>
  </v-alert>
</template>
